import { Link } from "@inertiajs/react"
import {
  ArrowUpDown,
  Clock,
  Download,
  FileStack,
  ImageIcon,
  Lock,
  MousePointerClick,
  PenLine,
  Scissors,
  ShieldCheck,
  Upload,
  UserX,
} from "lucide-react"

import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { SEO } from "@/components/seo"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

const trustSignals = [
  { icon: ShieldCheck, text: "Files processed in your browser" },
  { icon: Clock, text: "Auto-deleted after 24 hours" },
  { icon: UserX, text: "No signup required" },
]

const howItWorks = [
  {
    icon: MousePointerClick,
    title: "Pick a tool",
    description: "Choose what you need",
  },
  {
    icon: Upload,
    title: "Upload & edit",
    description: "Add files and configure",
  },
  { icon: Download, title: "Download", description: "Get your result" },
]

const tools = [
  {
    title: "Merge PDF",
    description: "Combine multiple PDF files into one document",
    icon: FileStack,
    href: "/merge-pdf",
  },
  {
    title: "Split PDF",
    description: "Extract pages or split PDF into multiple files",
    icon: Scissors,
    href: "/split-pdf",
  },
  {
    title: "Reorder Pages",
    description: "Rearrange pages in your PDF document",
    icon: ArrowUpDown,
    href: "/reorder-pdf",
  },
  {
    title: "Protect PDF",
    description: "Add password protection to your PDF",
    icon: Lock,
    href: "/protect-pdf",
  },
  {
    title: "Sign PDF",
    description: "Add your signature to PDF documents",
    icon: PenLine,
    href: "/sign-pdf",
  },
  {
    title: "Image to PDF",
    description: "Convert JPG and PNG images into a PDF document",
    icon: ImageIcon,
    href: "/image-to-pdf",
  },
]

export default function Home() {
  return (
    <>
      <SEO
        title="Free Online PDF Tools - Merge, Split & Sign | StitchPDF"
        description="Merge, split, sign, and protect PDFs online for free. No signup or software needed - works instantly in your browser."
        canonical="https://stitchpdf.com/"
        ogTitle="Free Online PDF Tools | StitchPDF"
        ogDescription="Merge, split, reorder, protect, sign, and convert PDF files for free. Works directly in your browser."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "StitchPDF",
            url: "https://stitchpdf.com",
            logo: "https://stitchpdf.com/logo.png",
            description:
              "Free online PDF tools. Merge, split, sign, and protect PDFs directly in your browser.",
            sameAs: [],
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              url: "https://stitchpdf.com/faq",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "StitchPDF",
            url: "https://stitchpdf.com",
            description:
              "Free online PDF tools for merging, splitting, signing, and protecting PDF files.",
            publisher: {
              "@type": "Organization",
              name: "StitchPDF",
            },
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight">
              PDF tools that work in your browser
            </h1>
            <p className="text-muted-foreground mt-4 text-lg">
              Merge, split, reorder, protect, sign, and convert PDF files.
              <br />
              No software to install, no signup required.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-8">
              {trustSignals.map((signal) => (
                <div
                  key={signal.text}
                  className="text-muted-foreground flex items-center gap-2 text-sm"
                >
                  <signal.icon className="h-4 w-4" aria-hidden="true" />
                  {signal.text}
                </div>
              ))}
            </div>
          </div>

          <div
            id="tools"
            className="mx-auto mt-16 grid max-w-4xl gap-6 md:grid-cols-3"
          >
            {tools.map((tool) => (
              <Link
                key={tool.href}
                href={tool.href}
                className="focus-visible:ring-ring/50 rounded-xl outline-none focus-visible:ring-[3px]"
              >
                <Card className="h-full transition-shadow hover:shadow-md">
                  <CardHeader>
                    <tool.icon
                      className="text-primary h-8 w-8"
                      aria-hidden="true"
                    />
                    <CardTitle className="mt-4">{tool.title}</CardTitle>
                    <CardDescription>{tool.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>

          <div className="mx-auto mt-16 max-w-4xl">
            <h2 className="text-center text-2xl font-bold">How it works</h2>
            <div className="mt-8 grid gap-8 md:grid-cols-3">
              {howItWorks.map((step, index) => (
                <div key={step.title} className="text-center">
                  <div className="bg-primary/10 mx-auto flex h-12 w-12 items-center justify-center rounded-full">
                    <step.icon
                      className="text-primary h-6 w-6"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Step {index + 1}
                  </p>
                  <h3 className="mt-1 font-semibold">{step.title}</h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  )
}
